import React, { useEffect, useState, useRef } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import {
  Check,
  AlertCircle,
  Loader2,
  Users,
  Target,
  BarChart3,
  Phone,
  Building,
  DollarSign,
  TrendingUp,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import { message } from 'antd';
import { getTenantId } from "../../../utils/auth";
import { HUBSPOT_INTEGRATION_AUTH_API, HUBSPOT_INTEGRATION_CALLBACK_API } from "../../../constants/API";

// Mock utilities (replace with your actual implementations)
// const axiosInstance = {
//   post: async (url: string, data: any) => {
//     console.log(`API call to ${url}:`, data);
//     // Simulate API delay
//     await new Promise((resolve) => setTimeout(resolve, 1500));
//     return { data: { success: true } };
//   },
// };

// const getTenantId = () => "tenant_123";



interface IntegrationStatus {
  salesforce: "idle" | "loading" | "connected" | "error";
  hubspot: "idle" | "loading" | "connected" | "error";
  pipedrive: "idle" | "loading" | "connected" | "error";
  zoho: "idle" | "loading" | "connected" | "error";
}

interface OAuthMessage {
  type: 'OAUTH_SUCCESS' | 'OAUTH_ERROR';
  platform: string;
  message?: string;
  data?: any;
}

interface CRMData {
  platform: string;
  contacts: number;
  deals: number;
  value: string;
  conversion: string;
}

interface HubSpotData {
  contacts: any[];
  companies: any[];
  deals: any[];
  analytics: {
    total_contacts: number;
    total_companies: number;
    total_deals: number;
    total_deal_value: number;
  } | null;
  lastSync: string | null;
}

const CRMConnections: React.FC = () => {
  const [status, setStatus] = useState<IntegrationStatus>({
    salesforce: "idle",
    hubspot: "idle",
    pipedrive: "idle",
    zoho: "idle",
  });

  const [crmData, setCrmData] = useState<CRMData[]>([]);
  const [hubspotData, setHubSpotData] = useState<HubSpotData>({
    contacts: [],
    companies: [],
    deals: [],
    analytics: null,
    lastSync: null,
  });
  const [hubspotSyncing, setHubSpotSyncing] = useState(false);
  const [hubspotExpanded, setHubSpotExpanded] = useState(false);

  // Mock CRM data for connected platforms
  const mockCRMData: Record<string, CRMData> = {
    salesforce: {
      platform: "Salesforce",
      contacts: 2847,
      deals: 156,
      value: "$890K",
      conversion: "23%",
    },
    hubspot: {
      platform: "HubSpot",
      contacts: 1923,
      deals: 89,
      value: "$450K",
      conversion: "18%",
    },
    pipedrive: {
      platform: "Pipedrive",
      contacts: 1456,
      deals: 67,
      value: "$320K",
      conversion: "28%",
    },
    zoho: {
      platform: "Zoho CRM",
      contacts: 987,
      deals: 43,
      value: "$180K",
      conversion: "21%",
    },
  };

  // Mock HubSpot detailed data
  const mockHubSpotData = {
    contacts: [
      {
        id: "1",
        properties: {
          firstname: { value: "John" },
          lastname: { value: "Doe" },
          email: { value: "john.doe@example.com" },
          company: { value: "TechCorp" },
          lifecyclestage: { value: "lead" },
          createdate: { value: "2024-01-15T10:00:00Z" },
        },
      },
      {
        id: "2",
        properties: {
          firstname: { value: "Jane" },
          lastname: { value: "Smith" },
          email: { value: "jane.smith@startup.com" },
          company: { value: "StartupXYZ" },
          lifecyclestage: { value: "customer" },
          createdate: { value: "2024-01-20T14:30:00Z" },
        },
      },
    ],
    companies: [
      {
        id: "1",
        properties: {
          name: { value: "TechCorp Inc." },
          domain: { value: "techcorp.com" },
          industry: { value: "Technology" },
          numberofemployees: { value: "250" },
          annualrevenue: { value: "5000000" },
        },
      },
      {
        id: "2",
        properties: {
          name: { value: "StartupXYZ" },
          domain: { value: "startupxyz.com" },
          industry: { value: "Software" },
          numberofemployees: { value: "15" },
          annualrevenue: { value: "500000" },
        },
      },
    ],
    deals: [
      {
        id: "1",
        properties: {
          dealname: { value: "Enterprise License Deal" },
          amount: { value: "50000" },
          dealstage: { value: "negotiation" },
          closedate: { value: "2024-03-15T00:00:00Z" },
          createdate: { value: "2024-01-10T09:00:00Z" },
        },
      },
      {
        id: "2",
        properties: {
          dealname: { value: "Consulting Project" },
          amount: { value: "25000" },
          dealstage: { value: "qualified" },
          closedate: { value: "2024-02-28T00:00:00Z" },
          createdate: { value: "2024-01-25T11:15:00Z" },
        },
      },
    ],
    analytics: {
      total_contacts: 1923,
      total_companies: 456,
      total_deals: 89,
      total_deal_value: 450000,
    },
  };

  const loadIntegrationStatus = async () => {
    console.log('Loading integration status from backend...');
    
    try {
      // Call your existing status endpoint
      const response = await axiosInstance.get('/api/integrations/status');
      
      console.log('Status API response:', response.data);
      
      if (response.data && response.data.integrations) {
        const integrations = response.data.integrations;
        
        // Update HubSpot status
        if (integrations.hubspot) {
          const hubspotStatus = integrations.hubspot.status;
          console.log('HubSpot status from API:', hubspotStatus);
          
          setStatus(prev => ({
            ...prev,
            hubspot: hubspotStatus === 'active' ? 'connected' : 'idle'
          }));
          
          console.log('Updated HubSpot status to:', hubspotStatus === 'ACTIVE' ? 'connected' : 'idle');
        } else {
          console.log('No HubSpot integration found');
          setStatus(prev => ({ ...prev, hubspot: 'idle' }));
        }
      } else {
        console.log('No integrations data in response');
        setStatus(prev => ({ ...prev, hubspot: 'idle' }));
      }
    } catch (error) {
      console.error('Error loading integration status:', error);
      // Don't set to error - just leave as idle if we can't load status
      setStatus(prev => ({ ...prev, hubspot: 'idle' }));
    }
  };

  // Load integration status when component mounts
  useEffect(() => {
    console.log('Component mounted - loading integration status');
    loadIntegrationStatus();
  }, []);

  useEffect(() => {
    // Update CRM data when platforms are connected
    const connectedPlatforms = Object.entries(status)
      .filter(([_, platformStatus]) => platformStatus === "connected")
      .map(([platform, _]) => mockCRMData[platform])
      .filter(Boolean);

    setCrmData(connectedPlatforms);
  }, [status]);

  const callbackProcessedRef = useRef(false);
  const handleHubSpotLogin = async () => {
    setStatus((prev) => ({ ...prev, hubspot: "loading" }));

    try {
      // Get the OAuth URL from your backend
      const response = await axiosInstance.post('/api/integrations/hubspot/initiate-oauth');

      if (response.data.status === "success") {
        const authUrl = response.data.auth_url;

        // Open popup window pointing to HubSpot
        const popup = window.open(
          authUrl,
          'hubspot-oauth',
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );

        // Check if popup was blocked
        if (!popup) {
          setStatus((prev) => ({ ...prev, hubspot: "error" }));
          message.error('Popup was blocked. Please allow popups and try again.');
          return;
        }

        // Listen for messages from the callback page
        const handleMessage = (event: MessageEvent<OAuthMessage>) => {
          // Verify origin for security
          if (event.origin !== window.location.origin) return;

          if (event.data.type === 'OAUTH_SUCCESS' && event.data.platform === 'hubspot') {
            setStatus((prev) => ({ ...prev, hubspot: "connected" }));
            message.success('HubSpot integration completed successfully!');
            setTimeout(() => {
              loadIntegrationStatus();
            }, 1000);
            window.removeEventListener('message', handleMessage);
          } else if (event.data.type === 'OAUTH_ERROR' && event.data.platform === 'hubspot') {
            setStatus((prev) => ({ ...prev, hubspot: "error" }));
            message.error(`Integration failed: ${event.data.message}`);
            window.removeEventListener('message', handleMessage);
          }
        };

        window.addEventListener('message', handleMessage);

        // Handle popup being closed manually
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            setStatus((prev) => ({ ...prev, hubspot: "idle" }));
            window.removeEventListener('message', handleMessage);
            clearInterval(checkClosed);
          }
        }, 1000);

      } else {
        setStatus((prev) => ({ ...prev, hubspot: "error" }));
        message.error('Failed to initiate HubSpot OAuth');
      }
    } catch (error) {
      console.error("Error initiating HubSpot OAuth", error);
      setStatus((prev) => ({ ...prev, hubspot: "error" }));
      message.error('Failed to initiate HubSpot OAuth');
    }
  };

  const handleHubSpotCallback = async (code: string, state: string) => {
    // Prevent double execution
    if (callbackProcessedRef.current) {
      console.log('Callback already processed, skipping');
      return;
    }

    callbackProcessedRef.current = true;
    console.log('Processing HubSpot callback in popup - ONCE');

    try {
      const response = await axiosInstance.post('/api/integrations/hubspot/oauth/callback', {
        code,
        state
      });

      console.log('Callback API response:', response.data);

      if (response.data.status === 'success') {
        // Send success message to parent window
        if (window.opener && !window.opener.closed) {
          console.log('Sending SUCCESS message to parent');
          window.opener.postMessage({
            type: 'OAUTH_SUCCESS',
            platform: 'hubspot',
            data: response.data
          }, window.location.origin);

          // Close popup after a short delay to ensure message is received
          setTimeout(() => window.close(), 500);
        }
      } else {
        // Send error message to parent window
        if (window.opener && !window.opener.closed) {
          console.log('Sending ERROR message to parent');
          window.opener.postMessage({
            type: 'OAUTH_ERROR',
            platform: 'hubspot',
            message: response.data.message || 'OAuth failed'
          }, window.location.origin);

          setTimeout(() => window.close(), 500);
        }
      }
    } catch (error) {
      console.error("Error handling HubSpot callback", error);

      let errorMessage = 'Callback processing failed';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        errorMessage = axiosError.response?.data?.message || axiosError.message || 'Request failed';
      }

      // Send error message to parent window
      if (window.opener && !window.opener.closed) {
        console.log('Sending ERROR message to parent (catch block)');
        window.opener.postMessage({
          type: 'OAUTH_ERROR',
          platform: 'hubspot',
          message: errorMessage
        }, window.location.origin);

        setTimeout(() => window.close(), 500);
      }
    }
  };


  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const oauthCallback = urlParams.get('oauth_callback');

    // If this page loaded with OAuth parameters and we're in a popup
    if ((code && state) || (oauthCallback === 'hubspot' && code && state)) {
      console.log('OAuth callback detected - processing once');

      // Clear URL parameters immediately to prevent re-execution
      const cleanUrl = window.location.pathname + window.location.hash.split('?')[0];
      window.history.replaceState({}, '', cleanUrl);

      // Process callback
      handleHubSpotCallback(code, state);
    }
  }, []);




  // HubSpot sync function
  const syncHubSpotData = async () => {
    setHubSpotSyncing(true);
    try {
      await axiosInstance.post("/api/hubspot/sync", {
        tenantId: getTenantId(),
        force_full_sync: false,
      });

      // Simulate updated data
      setHubSpotData(prev => ({
        ...prev,
        lastSync: new Date().toISOString(),
      }));
    } catch (error) {
      console.error("Error syncing HubSpot data", error);
    } finally {
      setHubSpotSyncing(false);
    }
  };

  const handleSalesforceLogin = async () => {
    setStatus((prev) => ({ ...prev, salesforce: "loading" }));
    try {
      await axiosInstance.post("/api/salesforce/auth", {
        tenantId: getTenantId(),
        instance_url: "https://yourcompany.my.salesforce.com",
        access_token: "mock_salesforce_access_token",
        refresh_token: "mock_salesforce_refresh_token",
        organization_id: "mock_org_id",
        user_id: "mock_user_id",
      });
      setStatus((prev) => ({ ...prev, salesforce: "connected" }));
    } catch (error) {
      console.error("Error with Salesforce integration", error);
      setStatus((prev) => ({ ...prev, salesforce: "error" }));
    }
  };

  const handlePipedriveLogin = async () => {
    setStatus((prev) => ({ ...prev, pipedrive: "loading" }));
    try {
      const apiKey = prompt("Enter your Pipedrive API key:");
      const companyDomain = prompt("Enter your Pipedrive company domain:");

      if (!apiKey || !companyDomain) {
        setStatus((prev) => ({ ...prev, pipedrive: "error" }));
        return;
      }

      await axiosInstance.post("/api/pipedrive/auth", {
        tenantId: getTenantId(),
        api_key: apiKey,
        company_domain: companyDomain,
        base_url: `https://${companyDomain}.pipedrive.com/api/v1`,
      });
      setStatus((prev) => ({ ...prev, pipedrive: "connected" }));
    } catch (error) {
      console.error("Error with Pipedrive integration", error);
      setStatus((prev) => ({ ...prev, pipedrive: "error" }));
    }
  };

  const handleZohoLogin = async () => {
    setStatus((prev) => ({ ...prev, zoho: "loading" }));
    try {
      await axiosInstance.post("/api/zoho/auth", {
        tenantId: getTenantId(),
        access_token: "mock_zoho_access_token",
        refresh_token: "mock_zoho_refresh_token",
        api_domain: "https://www.zohoapis.com",
        organization_id: "mock_zoho_org_id",
      });
      setStatus((prev) => ({ ...prev, zoho: "connected" }));
    } catch (error) {
      console.error("Error with Zoho integration", error);
      setStatus((prev) => ({ ...prev, zoho: "error" }));
    }
  };

  const getStatusIcon = (platformStatus: string) => {
    switch (platformStatus) {
      case "loading":
        return <Loader2 className="w-5 h-5 animate-spin text-blue-500" />;
      case "connected":
        return <Check className="w-5 h-5 text-green-500" />;
      case "error":
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getButtonText = (platform: string, platformStatus: string) => {
    switch (platformStatus) {
      case "loading":
        return "Connecting...";
      case "connected":
        return "Connected";
      case "error":
        return "Retry Connection";
      default:
        return `Connect ${platform}`;
    }
  };

  const getButtonStyle = (status: string, baseClasses: string) => {
    switch (status) {
      case "loading":
        return "bg-gray-400 cursor-not-allowed";
      case "connected":
        return "bg-green-600 cursor-default";
      case "error":
        return "bg-red-600 hover:bg-red-700";
      default:
        return baseClasses;
    }
  };


  const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Render HubSpot detailed view
  const renderHubSpotDetails = () => {
    if (status.hubspot !== "connected" || !hubspotExpanded) return null;

    return (
      <div className="mt-6 space-y-6">
        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm text-blue-700">Contacts</p>
                <p className="text-2xl font-bold text-blue-900">
                  {hubspotData.analytics?.total_contacts.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
            <div className="flex items-center gap-2">
              <Building className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm text-green-700">Companies</p>
                <p className="text-2xl font-bold text-green-900">
                  {hubspotData.analytics?.total_companies.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-purple-600" />
              <div>
                <p className="text-sm text-purple-700">Deals</p>
                <p className="text-2xl font-bold text-purple-900">
                  {hubspotData.analytics?.total_deals.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-xl p-4 border border-yellow-200">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="text-sm text-yellow-700">Deal Value</p>
                <p className="text-2xl font-bold text-yellow-900">
                  {hubspotData.analytics && formatCurrency(hubspotData.analytics.total_deal_value)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Data Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Contacts */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Contacts</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-2 text-gray-600">Name</th>
                    <th className="text-left p-2 text-gray-600">Company</th>
                    <th className="text-left p-2 text-gray-600">Stage</th>
                  </tr>
                </thead>
                <tbody>
                  {hubspotData.contacts.slice(0, 5).map((contact) => (
                    <tr key={contact.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-2">
                        <div>
                          <div className="font-medium text-gray-900">
                            {contact.properties.firstname?.value} {contact.properties.lastname?.value}
                          </div>
                          <div className="text-xs text-gray-500">{contact.properties.email?.value}</div>
                        </div>
                      </td>
                      <td className="p-2 text-gray-700">{contact.properties.company?.value}</td>
                      <td className="p-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {contact.properties.lifecyclestage?.value || 'Unknown'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Deals */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Deals</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-2 text-gray-600">Deal</th>
                    <th className="text-left p-2 text-gray-600">Amount</th>
                    <th className="text-left p-2 text-gray-600">Stage</th>
                  </tr>
                </thead>
                <tbody>
                  {hubspotData.deals.slice(0, 5).map((deal) => (
                    <tr key={deal.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-2">
                        <div className="font-medium text-gray-900">
                          {deal.properties.dealname?.value}
                        </div>
                      </td>
                      <td className="p-2 font-semibold text-green-600">
                        {deal.properties.amount?.value &&
                          formatCurrency(Number(deal.properties.amount.value))}
                      </td>
                      <td className="p-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {deal.properties.dealstage?.value || 'Unknown'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            CRM Platform Integration
          </h1>
        </div>

        {/* CRM Overview Dashboard */}
        {crmData.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-12 border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <BarChart3 className="w-6 h-6 mr-2 text-purple-500" />
              CRM Overview (Current Pipeline)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {crmData.map((data, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-100"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">
                      {data.platform}
                    </h3>
                    <span className="text-sm font-medium text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                      {data.conversion}
                    </span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-600">
                        <Users className="w-4 h-4 mr-1" />
                        Contacts
                      </div>
                      <span className="font-bold text-gray-900">
                        {data.contacts.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-600">
                        <Target className="w-4 h-4 mr-1" />
                        Active Deals
                      </div>
                      <span className="font-bold text-gray-900">
                        {data.deals}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-600">
                        <BarChart3 className="w-4 h-4 mr-1" />
                        Pipeline Value
                      </div>
                      <span className="font-bold text-gray-900 text-lg">
                        {data.value}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Integration Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Salesforce Integration */}
          <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:scale-105 transition-all duration-300 border border-gray-100">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-blue-50 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.621 5.367 11.988 11.988 11.988s11.987-5.367 11.987-11.988C24.004 5.367 18.637.001 12.017.001zM8.88 19.005c-.322 0-.582-.26-.582-.582s.26-.582.582-.582.582.26.582.582-.26.582-.582.582zm2.338-1.784c-.322 0-.582-.26-.582-.582s.26-.582.582-.582.582.26.582.582-.26.582-.582.582zm2.337-1.784c-.322 0-.582-.26-.582-.582s.26-.582.582-.582.582.26.582.582-.26.582-.582.582zm2.338-1.784c-.322 0-.582-.26-.582-.582s.26-.582.582-.582.582.26.582.582-.26.582-.582.582z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Salesforce</h3>
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                Connect your Salesforce org to sync leads, opportunities, accounts, and gain comprehensive sales pipeline visibility.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Data Available:</h4>
                <ul className="text-xs text-gray-600 space-y-1 text-left">
                  <li>• Leads, contacts, and account data</li>
                  <li>• Opportunity pipeline and forecasting</li>
                  <li>• Activity tracking and task management</li>
                  <li>• Custom objects and field mapping</li>
                  <li>• Sales team performance metrics</li>
                  <li>• Campaign and marketing attribution</li>
                </ul>
              </div>
              <div className="flex items-center justify-center mb-4">
                {getStatusIcon(status.salesforce)}
                <span className="ml-2 text-sm font-medium text-gray-700">
                  {status.salesforce === "connected" ? "Successfully Connected" :
                    status.salesforce === "error" ? "Connection Failed" :
                      status.salesforce === "loading" ? "Connecting..." : "Not Connected"}
                </span>
              </div>
              <button
                onClick={handleSalesforceLogin}
                disabled={status.salesforce === "loading" || status.salesforce === "connected"}
                className={`w-full py-3 px-6 rounded-xl text-white font-semibold transition-all duration-200 ${getButtonStyle(status.salesforce, "bg-blue-600 hover:bg-blue-700")}`}
              >
                {getButtonText("Salesforce", status.salesforce)}
              </button>
            </div>
          </div>

          {/* Enhanced HubSpot Integration */}
          <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:scale-105 transition-all duration-300 border border-gray-100">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-orange-50 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-orange-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.011 2.132c5.462 0 9.89 4.43 9.89 9.89 0 5.462-4.428 9.891-9.89 9.891-5.462 0-9.89-4.429-9.89-9.89 0-5.461 4.428-9.891 9.89-9.891zm0-2.132C5.382 0 0 5.382 0 12.022S5.382 24.043 12.011 24.043c6.629 0 12.011-5.382 12.011-12.021C24.022 5.382 18.64 0 12.011 0z" />
                  <circle cx="12.011" cy="12.022" r="3.291" />
                  <circle cx="18.132" cy="5.901" r="2.198" />
                  <path d="M15.587 8.446l-1.809 1.809c-.245-.245-.543-.43-.874-.543l1.809-1.809c.113.331.298.629.543.874.245.245.543.43.874.543-.113-.331-.298-.629-.543-.874z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">HubSpot</h3>
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                Integrate HubSpot CRM to sync your inbound marketing data, sales pipeline, and customer service interactions.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Data Available:</h4>
                <ul className="text-xs text-gray-600 space-y-1 text-left">
                  <li>• Contact lifecycle and lead scoring</li>
                  <li>• Deal pipeline and revenue tracking</li>
                  <li>• Marketing automation workflows</li>
                  <li>• Website analytics and form data</li>
                  <li>• Email marketing performance</li>
                  <li>• Customer service ticket data</li>
                </ul>
              </div>


              {/* Status display */}
              <div className="flex items-center justify-center mb-4">
                <span className="ml-2 text-sm font-medium text-gray-700">
                  {status.hubspot === "connected" ? "Successfully Connected" :
                    status.hubspot === "error" ? "Connection Failed" :
                      status.hubspot === "loading" ? "Connecting..." : "Not Connected"}
                </span>
              </div>

              {/* Show last sync info if connected */}
              {status.hubspot === "connected" && hubspotData.lastSync && (
                <div className="text-xs text-gray-500 mb-4">
                  Last sync: {formatDate(hubspotData.lastSync)}
                </div>
              )}

              <div className="space-y-2">
                <button
                  onClick={handleHubSpotLogin}
                  disabled={status.hubspot === "loading" || status.hubspot === "connected"}
                  className={`w-full py-3 px-6 rounded-xl text-white font-semibold transition-all duration-200 ${getButtonStyle(status.hubspot, "bg-orange-600 hover:bg-orange-700")}`}
                >
                  {getButtonText("HubSpot", status.hubspot)}
                </button>

                {/* Additional actions for connected HubSpot */}
                {status.hubspot === "connected" && (
                  <div className="flex gap-2">
                    <button
                      onClick={syncHubSpotData}
                      disabled={hubspotSyncing}
                      className="flex-1 py-2 px-4 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors duration-200 text-sm font-medium flex items-center justify-center gap-1"
                    >
                      {hubspotSyncing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      Sync
                    </button>
                    <button
                      onClick={() => setHubSpotExpanded(!hubspotExpanded)}
                      className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-sm font-medium"
                    >
                      {hubspotExpanded ? "Hide Details" : "View Details"}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Enhanced HubSpot Details */}
            {renderHubSpotDetails()}
          </div>

          {/* Pipedrive Integration */}
          <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:scale-105 transition-all duration-300 border border-gray-100">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-green-50 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.951 6.534c-.345-.345-.951-.345-1.296 0l-4.5 4.5c-.345.345-.345.951 0 1.296.345.345.951.345 1.296 0l4.5-4.5c.345-.345.345-.951 0-1.296zM12 0C5.383 0 0 5.383 0 12s5.383 12 12 12 12-5.383 12-12S18.617 0 12 0zm0 21c-4.963 0-9-4.037-9-9s4.037-9 9-9 9 4.037 9 9-4.037 9-9 9z" />
                  <circle cx="12" cy="12" r="6" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Pipedrive</h3>
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                Connect Pipedrive to access your sales pipeline data, activity tracking, and deal management information.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Data Available:</h4>
                <ul className="text-xs text-gray-600 space-y-1 text-left">
                  <li>• Pipeline and deal stage tracking</li>
                  <li>• Person and organization data</li>
                  <li>• Activity and communication logs</li>
                  <li>• Sales performance analytics</li>
                  <li>• Goal tracking and forecasting</li>
                  <li>• Product and pricing data</li>
                </ul>
              </div>
              <div className="flex items-center justify-center mb-4">
                {getStatusIcon(status.pipedrive)}
                <span className="ml-2 text-sm font-medium text-gray-700">
                  {status.pipedrive === "connected" ? "Successfully Connected" :
                    status.pipedrive === "error" ? "Connection Failed" :
                      status.pipedrive === "loading" ? "Connecting..." : "Not Connected"}
                </span>
              </div>
              <button
                onClick={handlePipedriveLogin}
                disabled={status.pipedrive === "loading" || status.pipedrive === "connected"}
                className={`w-full py-3 px-6 rounded-xl text-white font-semibold transition-all duration-200 ${getButtonStyle(status.pipedrive, "bg-green-600 hover:bg-green-700")}`}
              >
                {getButtonText("Pipedrive", status.pipedrive)}
              </button>
            </div>
          </div>

          {/* Zoho CRM Integration */}
          <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:scale-105 transition-all duration-300 border border-gray-100">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-red-50 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.568 8.16c-.169 1.58-.542 3.096-1.09 4.64-.547 1.544-1.267 2.991-2.133 4.32-.867 1.33-1.884 2.484-3.004 3.484-1.12 1-2.347 1.832-3.633 2.484-.064.032-.13.063-.198.094-.017-.041-.032-.082-.046-.125-.46-1.42-.718-2.914-.753-4.469-.035-1.555.144-3.082.525-4.547.38-1.465.961-2.868 1.713-4.176.752-1.308 1.675-2.52 2.733-3.605 1.058-1.085 2.25-2.042 3.539-2.844.129-.08.259-.159.39-.236.157.331.289.678.389 1.04.1.361.168.729.204 1.1.036.37.04.742.011 1.111-.029.37-.095.735-.198 1.094-.103.359-.243.71-.417 1.049-.174.339-.382.666-.622.975z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Zoho CRM</h3>
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                Integrate Zoho CRM to synchronize your customer data, sales processes, and business workflow automation.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Data Available:</h4>
                <ul className="text-xs text-gray-600 space-y-1 text-left">
                  <li>• Lead and contact management</li>
                  <li>• Deal and opportunity tracking</li>
                  <li>• Account and territory management</li>
                  <li>• Workflow automation rules</li>
                  <li>• Sales forecasting and reporting</li>
                  <li>• Integration with Zoho ecosystem</li>
                </ul>
              </div>
              <div className="flex items-center justify-center mb-4">
                {getStatusIcon(status.zoho)}
                <span className="ml-2 text-sm font-medium text-gray-700">
                  {status.zoho === "connected" ? "Successfully Connected" :
                    status.zoho === "error" ? "Connection Failed" :
                      status.zoho === "loading" ? "Connecting..." : "Not Connected"}
                </span>
              </div>
              <button
                onClick={handleZohoLogin}
                disabled={status.zoho === "loading" || status.zoho === "connected"}
                className={`w-full py-3 px-6 rounded-xl text-white font-semibold transition-all duration-200 ${getButtonStyle(status.zoho, "bg-red-600 hover:bg-red-700")}`}
              >
                {getButtonText("Zoho CRM", status.zoho)}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CRMConnections;